import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection,
  AutoTotal,
  parseMoney
} from '@/components/WorkbookPage';
import { Building2, CreditCard, Smartphone, Banknote } from 'lucide-react';

const BILL_ITEMS = ['Rent/Mortgage', 'Utilities', 'Phone', 'Insurance', 'Car Payment', 'Subscriptions', 'Min Debt Payments', 'Other Fixed'];
const billKey = (item) => `bill_${item.toLowerCase().replace(/\s/g, '_')}`;

export const Rule2Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule2 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule2', { ...data, [field]: value });
  };

  const billsTotal = BILL_ITEMS.reduce((sum, it) => sum + parseMoney(data[billKey(it)]), 0);

  const updateBillField = (item, value) => {
    const next = { ...data, [billKey(item)]: value };
    next.billsAccountAmount = BILL_ITEMS
      .reduce((sum, it) => sum + parseMoney(it === item ? value : data[billKey(it)]), 0)
      .toFixed(2);
    updateFormData('rule2', next);
  };
  
  return (
    <WorkbookPage pageId="rule-2" prevPage="rule-1" nextPage="rule-3">
      <RuleHeader ruleNumber="2" title="The Multiple Account System" section="stability" />
      
      {/* Overview */}
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Instead of putting all your money in one account and hoping for the best, you split 
        your money into separate "buckets" with specific jobs. This simple system prevents 
        overspending and makes sure your bills get paid.
      </Paragraph>
      <Paragraph>
        <strong>The three accounts are:</strong>
      </Paragraph>
      
      <div className="grid gap-4 my-6">
        <div className="flex items-start gap-4 bg-secondary/50 rounded-lg p-4">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
            <Building2 className="h-6 w-6 text-destructive" />
          </div>
          <div>
            <h4 className="font-semibold">1. Bills Account</h4>
            <p className="text-sm text-muted-foreground">
              Money for fixed, must-pay expenses: rent, utilities, car payment, insurance, phone bill. 
              This money is already "spent"—don't touch it.
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-4 bg-secondary/50 rounded-lg p-4">
          <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
            <CreditCard className="h-6 w-6 text-accent" />
          </div>
          <div>
            <h4 className="font-semibold">2. Spending Account</h4>
            <p className="text-sm text-muted-foreground">
              Money for daily life: groceries, gas, personal items, entertainment. 
              This is your "allowance"—when it's gone, it's gone.
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-4 bg-secondary/50 rounded-lg p-4">
          <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center shrink-0">
            <Banknote className="h-6 w-6 text-success" />
          </div>
          <div>
            <h4 className="font-semibold">3. Savings/Buffer Account</h4>
            <p className="text-sm text-muted-foreground">
              Money set aside for emergencies and future goals. Even $10/week adds up. 
              This account protects you when life happens.
            </p>
          </div>
        </div>
      </div>
      
      {/* Why It Matters */}
      <SectionTitle>Why It Matters</SectionTitle>
      <Paragraph>
        When all your money sits in one account, it's easy to accidentally spend bill money 
        on other things. Then when rent is due, you're scrambling. The multiple account system 
        removes willpower from the equation—you literally can't spend money that isn't there.
      </Paragraph>
      
      <Callout type="success">
        <strong>This system works for any income level.</strong> Even if you're just starting 
        out or rebuilding, separating money into jobs helps you see exactly what you have to work with.
      </Callout>
      
      {/* Banking options for underbanked areas */}
      <SectionTitle>Banking Options (Even Without a Traditional Bank)</SectionTitle>
      <Paragraph>
        Not everyone has easy access to a bank. Here are options that work:
      </Paragraph>
      
      <div className="grid sm:grid-cols-2 gap-4 my-6">
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="h-4 w-4 text-primary" />
            <h4 className="font-semibold text-sm">Online Banks</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Chime, Varo, Current—no minimum balance, no monthly fees, easy app setup. 
            Many offer early direct deposit.
          </p>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-primary" />
            <h4 className="font-semibold text-sm">Credit Unions</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Member-owned, often more flexible than big banks. Lower fees, better service. 
            Search for credit unions in your area.
          </p>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-4 w-4 text-primary" />
            <h4 className="font-semibold text-sm">Prepaid Debit Cards</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Bluebird, Serve, Green Dot—no credit check, load money at many stores. 
            Good for separating spending money.
          </p>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Banknote className="h-4 w-4 text-primary" />
            <h4 className="font-semibold text-sm">Cash Envelope System</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            If banks aren't an option, use labeled envelopes. "Bills," "Spending," "Savings." 
            Keep savings somewhere safe.
          </p>
        </div>
      </div>
      
      {/* Examples */}
      <SectionTitle>Real-Life Example</SectionTitle>
      
      <ExampleBox title="How Maria Sets Up Her Accounts">
        <p><strong>Maria</strong> earns $2,400/month. Here's how she splits it:</p>
        <div className="mt-3 space-y-2">
          <div className="flex justify-between text-sm">
            <span>Bills Account:</span>
            <span className="font-semibold">$1,400 (58%)</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground pl-4">
            <span>- Rent: $850</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground pl-4">
            <span>- Utilities: $150</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground pl-4">
            <span>- Phone: $80</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground pl-4">
            <span>- Insurance: $120</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground pl-4">
            <span>- Subscriptions: $50</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground pl-4">
            <span>- Min debt payments: $150</span>
          </div>
          <div className="h-px bg-border my-2" />
          <div className="flex justify-between text-sm">
            <span>Spending Account:</span>
            <span className="font-semibold">$800 (33%)</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground pl-4">
            <span>- Groceries, gas, personal</span>
          </div>
          <div className="h-px bg-border my-2" />
          <div className="flex justify-between text-sm">
            <span>Savings/Buffer:</span>
            <span className="font-semibold">$200 (9%)</span>
          </div>
        </div>
      </ExampleBox>
      
      {/* Step by Step */}
      <SectionTitle>Step-by-Step Instructions</SectionTitle>
      <StepList steps={[
        "List all your fixed monthly bills (rent, utilities, insurance, phone, subscriptions, minimum debt payments).",
        "Add them up—this is your Bills Account target.",
        "Subtract bills from your monthly income. What's left is for Spending and Savings.",
        "Decide how much goes to Savings (start with even 5-10% if money is tight).",
        "The remainder is your Spending allowance.",
        "Open separate accounts (or use envelopes) for each category.",
        "Set up automatic transfers on payday to move money to the right accounts."
      ]} />
      
      {/* Worksheet */}
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Account Setup Worksheet</h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-3">Monthly Income (from Rule 1):</label>
            <input
              type="text"
              className="worksheet-input"
              placeholder="$___"
              value={data.monthlyIncome || ''}
              onChange={(e) => updateData('monthlyIncome', e.target.value)}
            />
          </div>
          
          <div className="bg-destructive/5 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-3">Bills Account - Fixed Expenses:</h4>
            <div className="grid grid-cols-2 gap-3">
              {BILL_ITEMS.map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-24">{item}:</span>
                  <input
                    type="text"
                    className="flex-1 border-b border-dashed border-border bg-transparent py-1 text-sm focus:outline-none focus:border-primary"
                    placeholder="$___"
                    value={data[billKey(item)] || ''}
                    onChange={(e) => updateBillField(item, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-border">
              <AutoTotal
                label="Total Bills Account Amount:"
                value={billsTotal}
                hint="Adds up automatically from the fixed expenses above"
                testId="rule2-bills-total"
              />
            </div>
          </div>
          
          <div className="bg-accent/5 rounded-lg p-4">
            <WorksheetInput
              label="Spending Account Amount:"
              placeholder="$___"
              value={data.spendingAccountAmount || ''}
              onChange={(v) => updateData('spendingAccountAmount', v)}
            />
            <p className="text-xs text-muted-foreground">Groceries, gas, personal items, entertainment</p>
          </div>
          
          <div className="bg-success/5 rounded-lg p-4">
            <WorksheetInput
              label="Savings/Buffer Account Amount:"
              placeholder="$___"
              value={data.savingsAccountAmount || ''}
              onChange={(v) => updateData('savingsAccountAmount', v)}
            />
            <p className="text-xs text-muted-foreground">Emergency fund, future goals</p>
          </div>
          
          <WorksheetInput
            label="Where will you keep these accounts? (Bank name, prepaid card, envelopes, etc.)"
            placeholder="Describe your setup..."
            value={data.bankName || ''}
            onChange={(v) => updateData('bankName', v)}
            multiline
          />
        </div>
      </div>
      
      {/* Checklist */}
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem 
          checked={data.checklist1 || false}
          onChange={(v) => updateData('checklist1', v)}
        >
          I've listed all my fixed monthly bills
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist2 || false}
          onChange={(v) => updateData('checklist2', v)}
        >
          I know how much needs to go to my Bills Account
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist3 || false}
          onChange={(v) => updateData('checklist3', v)}
        >
          I've decided on my Spending and Savings amounts
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist4 || false}
          onChange={(v) => updateData('checklist4', v)}
        >
          I have a plan for where to keep these accounts
        </CheckboxItem>
      </div>
      
      {/* Recap */}
      <RecapBox items={[
        "Bills Account = fixed expenses only (already 'spent')",
        "Spending Account = your weekly/daily allowance",
        "Savings/Buffer = protection for emergencies",
        "Separate accounts prevent accidental overspending"
      ]} />
      
      {/* Affirmation */}
      <AffirmationBox>
        I am in control of where my money goes. By giving every dollar a job, I create stability and peace of mind.
      </AffirmationBox>
      
      {/* Optional Reflection */}
      <OptionalSection>
        <p className="mb-4">Consider:</p>
        <ul className="list-disc list-inside space-y-2 text-sm">
          <li>Have you ever had a time when bill money got "accidentally" spent on something else?</li>
          <li>How would it feel to know that your bills are always covered?</li>
          <li>What past money stress might this system help you avoid?</li>
        </ul>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="Write anything that comes to mind..."
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
